import { sanitizeSameOriginPath } from './url.utils';

// `inviteToken`/`continueUrl` also appear in `filteredFromPageChangeQueryParams`
// (query-params.utils.ts), which drops them on ordinary navigation. The two lists
// apply at different moments and don't conflict: explicit forwarding here carries
// the param across a deliberate cross-route auth navigation (e.g. /signup →
// /enterprise-sso), while the deny-list strips it from incidental page changes so
// it's consumed exactly once at its destination.
const FORWARDED_AUTH_PARAMS = ['inviteToken', 'continueUrl', 'reason'] as const;

/** The `reason` value marking a session cleared because the organization admits only enterprise SSO. */
export const ENTERPRISE_SSO_REQUIRED_REASON = 'enterprise-sso-required';

/**
 * Where to send a user after auth completes, derived from the URL params they
 * carried in. `continueUrl` wins when present (set explicitly by the email/password
 * flow); otherwise an `inviteToken` is folded into `/accept-invitation?token=…` so
 * SSO sign-ups land on the same acceptance page as the email-link path. The result
 * passes through the same-origin guard.
 */
export const deriveAuthContinueUrl = (
  inviteToken: string | null | undefined,
  continueUrl: string | null | undefined,
): string | null => {
  const candidate = continueUrl ?? (inviteToken ? `/accept-invitation?token=${encodeURIComponent(inviteToken)}` : null);
  return sanitizeSameOriginPath(candidate);
};

/**
 * Pull auth-relevant params (`inviteToken`, `continueUrl`) out of the current URL
 * so they can be threaded onto the next link/redirect. Defaults to
 * `window.location.search` to mirror how `useUrlQueryParam` reads URL state.
 */
export const getForwardedAuthParams = (search: string = window.location.search): URLSearchParams => {
  const current = new URLSearchParams(search);
  const forwarded = new URLSearchParams();
  for (const param of FORWARDED_AUTH_PARAMS) {
    const value = current.get(param);
    if (value) forwarded.set(param, value);
  }
  return forwarded;
};

/** Append forwarded auth params to a path; no-op when none are present. */
export const withForwardedAuthParams = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query === '' ? path : `${path}?${query}`;
};
